import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Button, Spinner } from "reactstrap";
import { toast } from "react-toastify";
import {
    exportSoniaReportCsvService,
    getSoniaQuestionsService,
    getSoniaReportAllService,
    getSoniaReportCompanyService,
    resolveSoniaQuestionService,
    saveSoniaActivityService,
} from "../../../../Services/CRMAdminServices/CRMOperator";

/**
 * Sonia coaching reports — Fintrack only.
 *
 * The top of the screen is the master scorecard: every client for the month,
 * ranked by appointments booked. Open a client and you get their month, their
 * brokers, their campaigns, the last twelve months, and the plain-English
 * coaching the API writes from those same numbers.
 *
 * Underneath each client sit the questions Sonia could not answer. Answering
 * one here writes it into that client's knowledge base, which is the loop that
 * makes the base self-updating — and the thing Regathered do not have.
 */

const MONTHS = ["January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"];

const num = (v: any) => Number(v ?? 0).toLocaleString();
const pct = (v: any) => `${Number(v ?? 0).toFixed(1)}%`;

const CRMSoniaReports = () => {
    const now = new Date();
    const [year, setYear] = useState(now.getFullYear());
    const [month, setMonth] = useState(now.getMonth() + 1);

    const [report, setReport] = useState<any>(null);
    const [loading, setLoading] = useState(true);

    const [openId, setOpenId] = useState<number | null>(null);
    const [detail, setDetail] = useState<any>(null);
    const [questions, setQuestions] = useState<any[]>([]);
    const [detailLoading, setDetailLoading] = useState(false);

    const [answers, setAnswers] = useState<Record<number, string>>({});
    const [entry, setEntry] = useState<any | null>(null);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r: any = await getSoniaReportAllService({ year, month });
            setReport(r?.data ?? null);
        } catch (e: any) {
            toast.error(e?.response?.data?.message || "Could not load the Sonia report.");
        } finally {
            setLoading(false);
        }
    }, [year, month]);

    useEffect(() => { load(); }, [load]);

    // Close whatever is open when the month changes — the detail belongs to a month.
    useEffect(() => { setOpenId(null); setDetail(null); setQuestions([]); setEntry(null); }, [year, month]);

    const openCompany = async (companyId: number) => {
        if (openId === companyId) { setOpenId(null); setDetail(null); setQuestions([]); return; }
        setOpenId(companyId);
        setDetailLoading(true);
        try {
            const [r, q]: any[] = await Promise.all([
                getSoniaReportCompanyService(companyId, { year, month }),
                getSoniaQuestionsService(companyId, { status: "Open" }),
            ]);
            setDetail(r?.data ?? null);
            setQuestions(q?.data ?? []);
        } catch (e: any) {
            toast.error(e?.response?.data?.message || "Could not load that client.");
        } finally {
            setDetailLoading(false);
        }
    };

    const refreshCompany = async (companyId: number) => {
        const [r, q]: any[] = await Promise.all([
            getSoniaReportCompanyService(companyId, { year, month }),
            getSoniaQuestionsService(companyId, { status: "Open" }),
        ]);
        setDetail(r?.data ?? null);
        setQuestions(q?.data ?? []);
    };

    const resolve = async (question: any, action: "Add" | "Ignore") => {
        const answer = (answers[question.questionId] ?? question.suggestedAnswer ?? "").trim();
        if (action === "Add" && !answer) {
            toast.error("Write the answer first — that is what goes into the knowledge base.");
            return;
        }
        try {
            await resolveSoniaQuestionService({
                questionId: question.questionId,
                companyId: question.companyId,
                action,
                answer: action === "Add" ? answer : null,
            });
            toast.success(action === "Add" ? "Added to the knowledge base." : "Parked.");
            await refreshCompany(question.companyId);
            await load();
        } catch (e: any) {
            toast.error(e?.response?.data?.message || "Could not save that.");
        }
    };

    const saveActivity = async () => {
        if (!entry) return;
        setSaving(true);
        try {
            await saveSoniaActivityService({
                companyId: entry.companyId,
                campaign: entry.campaign || "All",
                periodYear: year,
                periodMonth: month,
                messagesSent: Number(entry.messagesSent) || 0,
                replies: Number(entry.replies) || 0,
                conversations: Number(entry.conversations) || 0,
                qualified: Number(entry.qualified) || 0,
                booked: Number(entry.booked) || 0,
                showedUp: Number(entry.showedUp) || 0,
                settled: Number(entry.settled) || 0,
                escalations: Number(entry.escalations) || 0,
                optOuts: Number(entry.optOuts) || 0,
            });
            toast.success("Saved.");
            setEntry(null);
            await load();
            if (openId) await refreshCompany(openId);
        } catch (e: any) {
            toast.error(e?.response?.data?.message || "Could not save the month.");
        } finally {
            setSaving(false);
        }
    };

    const exportCsv = async () => {
        try {
            const r: any = await exportSoniaReportCsvService({ year, month });
            const url = window.URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = `Sonia ${year}-${String(month).padStart(2, "0")}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch {
            toast.error("Could not build the export.");
        }
    };

    const years = useMemo(() => {
        const out: number[] = [];
        for (let y = now.getFullYear(); y >= now.getFullYear() - 4; y--) out.push(y);
        return out;
    }, [now]);

    const companies = report?.companies ?? [];
    const totals = report?.totals ?? {};

    return (
        <div className="p-3">
            <div className="d-flex align-items-center justify-content-between mb-3">
                <h3 className="f-w-900 mb-0" style={{ color: "blue" }}>Sonia coaching reports</h3>
                <div className="d-flex align-items-center gap-2">
                    <select className="form-select form-select-sm" style={{ width: 140 }}
                        value={month} onChange={(e) => setMonth(Number(e.target.value))}>
                        {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                    </select>
                    <select className="form-select form-select-sm" style={{ width: 100 }}
                        value={year} onChange={(e) => setYear(Number(e.target.value))}>
                        {years.map((y) => <option key={y} value={y}>{y}</option>)}
                    </select>
                    <Button color="primary" size="sm" onClick={exportCsv}>Export CSV</Button>
                </div>
            </div>

            {/* The month across every client */}
            <div className="d-flex mb-3" style={{ gap: 12, flexWrap: "wrap" }}>
                <Tile label="Clients sending" value={num(totals.companies)} />
                <Tile label="Messages" value={num(totals.messagesSent)} />
                <Tile label="Replies" value={`${num(totals.replies)} (${pct(totals.replyRate)})`} />
                <Tile label="Booked" value={num(totals.booked)} strong />
                <Tile label="Settled" value={num(totals.settled)} strong />
                <Tile label="Opt outs" value={num(totals.optOuts)} />
                <Tile label="Open questions" value={num(totals.openQuestions)} />
            </div>

            <div className="p-3" style={panel}>
                {loading ? (
                    <div className="text-center py-4"><Spinner size="sm" /></div>
                ) : companies.length === 0 ? (
                    <div className="text-muted py-3">No clients yet.</div>
                ) : (
                    <div className="table-responsive">
                        <table className="table table-sm align-middle mb-0">
                            <thead>
                                <tr>
                                    <th style={{ width: "22%" }}>Client</th>
                                    <th className="text-end">Sent</th>
                                    <th className="text-end">Replies</th>
                                    <th className="text-end">Reply %</th>
                                    <th className="text-end">Booked</th>
                                    <th className="text-end">Book %</th>
                                    <th className="text-end">Showed</th>
                                    <th className="text-end">Settled</th>
                                    <th className="text-end">Opt outs</th>
                                    <th className="text-end">Questions</th>
                                    <th style={{ width: 90 }} />
                                </tr>
                            </thead>
                            <tbody>
                                {companies.map((c: any) => (
                                    <Fragment key={c.companyId}>
                                        <tr style={{ cursor: "pointer" }} onClick={() => openCompany(c.companyId)}>
                                            <td className="f-w-600">
                                                {c.businessName}
                                                {!c.isActive && <Badge color="light" className="ms-2 text-dark">Off</Badge>}
                                            </td>
                                            <td className="text-end">{num(c.messagesSent)}</td>
                                            <td className="text-end">{num(c.replies)}</td>
                                            <td className="text-end">{pct(c.replyRate)}</td>
                                            <td className="text-end f-w-600">{num(c.booked)}</td>
                                            <td className="text-end">{pct(c.bookingRate)}</td>
                                            <td className="text-end">{num(c.showedUp)}</td>
                                            <td className="text-end f-w-600">{num(c.settled)}</td>
                                            <td className="text-end">{num(c.optOuts)}</td>
                                            <td className="text-end">
                                                {c.openQuestions > 0
                                                    ? <Badge color="warning" className="text-dark">{c.openQuestions}</Badge>
                                                    : <span className="text-muted">—</span>}
                                            </td>
                                            <td className="text-end">
                                                <Button color="link" size="sm" className="p-0"
                                                    onClick={(e) => { e.stopPropagation(); openCompany(c.companyId); }}>
                                                    {openId === c.companyId ? "Close" : "Open"}
                                                </Button>
                                            </td>
                                        </tr>

                                        {openId === c.companyId && (
                                            <tr>
                                                <td colSpan={11} style={{ background: "#fafbff" }}>
                                                    {detailLoading ? (
                                                        <div className="text-center py-3"><Spinner size="sm" /></div>
                                                    ) : (
                                                        <div className="p-2">

                                                            {/* What the numbers mean */}
                                                            {(detail?.coaching ?? []).length > 0 && (
                                                                <div className="mb-3 p-3" style={coachBox}>
                                                                    <div className="f-w-700 mb-2" style={{ fontSize: 13 }}>Coaching</div>
                                                                    <ul className="mb-0 ps-3">
                                                                        {detail.coaching.map((line: string, i: number) => (
                                                                            <li key={i} style={{ fontSize: 13, marginBottom: 4 }}>{line}</li>
                                                                        ))}
                                                                    </ul>
                                                                </div>
                                                            )}

                                                            <div className="row g-3">
                                                                {/* Broker by broker */}
                                                                <div className="col-md-6">
                                                                    <div className="f-w-700 mb-2" style={{ fontSize: 13 }}>Broker by broker</div>
                                                                    {(detail?.brokers ?? []).length === 0 ? (
                                                                        <div className="text-muted" style={{ fontSize: 13 }}>Nothing split by broker this month.</div>
                                                                    ) : (
                                                                        <table className="table table-sm mb-0">
                                                                            <thead>
                                                                                <tr>
                                                                                    <th>Broker</th>
                                                                                    <th className="text-end">Sent</th>
                                                                                    <th className="text-end">Replies</th>
                                                                                    <th className="text-end">Booked</th>
                                                                                    <th className="text-end">Showed</th>
                                                                                    <th className="text-end">Settled</th>
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody>
                                                                                {detail.brokers.map((b: any) => (
                                                                                    <tr key={b.staffId}>
                                                                                        <td className="f-w-600">{b.brokerName}</td>
                                                                                        <td className="text-end">{num(b.messagesSent)}</td>
                                                                                        <td className="text-end">{num(b.replies)}</td>
                                                                                        <td className="text-end f-w-600">{num(b.booked)}</td>
                                                                                        <td className="text-end">{num(b.showedUp)}</td>
                                                                                        <td className="text-end">{num(b.settled)}</td>
                                                                                    </tr>
                                                                                ))}
                                                                            </tbody>
                                                                        </table>
                                                                    )}
                                                                </div>

                                                                {/* Campaign by campaign */}
                                                                <div className="col-md-6">
                                                                    <div className="f-w-700 mb-2" style={{ fontSize: 13 }}>Activation and Reactivation</div>
                                                                    {(detail?.campaigns ?? []).length === 0 ? (
                                                                        <div className="text-muted" style={{ fontSize: 13 }}>Nothing split by campaign this month.</div>
                                                                    ) : (
                                                                        <table className="table table-sm mb-0">
                                                                            <thead>
                                                                                <tr>
                                                                                    <th>Campaign</th>
                                                                                    <th className="text-end">Sent</th>
                                                                                    <th className="text-end">Replies</th>
                                                                                    <th className="text-end">Reply %</th>
                                                                                    <th className="text-end">Booked</th>
                                                                                    <th className="text-end">Settled</th>
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody>
                                                                                {detail.campaigns.map((c2: any) => (
                                                                                    <tr key={c2.campaign}>
                                                                                        <td className="f-w-600">{c2.campaign}</td>
                                                                                        <td className="text-end">{num(c2.messagesSent)}</td>
                                                                                        <td className="text-end">{num(c2.replies)}</td>
                                                                                        <td className="text-end">{pct(c2.replyRate)}</td>
                                                                                        <td className="text-end f-w-600">{num(c2.booked)}</td>
                                                                                        <td className="text-end">{num(c2.settled)}</td>
                                                                                    </tr>
                                                                                ))}
                                                                            </tbody>
                                                                        </table>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            {/* The last twelve months */}
                                                            {(detail?.trend ?? []).length > 1 && (
                                                                <div className="mt-3">
                                                                    <div className="f-w-700 mb-2" style={{ fontSize: 13 }}>The last twelve months</div>
                                                                    <table className="table table-sm mb-0">
                                                                        <thead>
                                                                            <tr>
                                                                                <th>Month</th>
                                                                                <th className="text-end">Sent</th>
                                                                                <th className="text-end">Replies</th>
                                                                                <th className="text-end">Booked</th>
                                                                                <th className="text-end">Settled</th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            {detail.trend.map((t: any) => (
                                                                                <tr key={`${t.periodYear}-${t.periodMonth}`}>
                                                                                    <td>{MONTHS[t.periodMonth - 1]?.slice(0, 3)} {t.periodYear}</td>
                                                                                    <td className="text-end">{num(t.messagesSent)}</td>
                                                                                    <td className="text-end">{num(t.replies)}</td>
                                                                                    <td className="text-end f-w-600">{num(t.booked)}</td>
                                                                                    <td className="text-end">{num(t.settled)}</td>
                                                                                </tr>
                                                                            ))}
                                                                        </tbody>
                                                                    </table>
                                                                </div>
                                                            )}

                                                            {/* The knowledge base loop */}
                                                            <div className="mt-3">
                                                                <div className="f-w-700 mb-2" style={{ fontSize: 13 }}>
                                                                    What Sonia could not answer
                                                                </div>
                                                                {questions.length === 0 ? (
                                                                    <div className="text-muted" style={{ fontSize: 13 }}>
                                                                        Nothing open. Every question Sonia has hit is already in their knowledge base.
                                                                    </div>
                                                                ) : (
                                                                    questions.map((q: any) => (
                                                                        <div key={q.questionId} className="mb-2 p-2" style={qBox}>
                                                                            <div className="d-flex justify-content-between align-items-start mb-2">
                                                                                <div className="f-w-600" style={{ fontSize: 13 }}>{q.questionText}</div>
                                                                                <Badge color="light" className="text-dark ms-2">
                                                                                    asked {q.timesAsked}x
                                                                                </Badge>
                                                                            </div>
                                                                            <textarea
                                                                                className="form-control form-control-sm mb-2"
                                                                                rows={2}
                                                                                placeholder="The answer Sonia should give from now on"
                                                                                defaultValue={q.suggestedAnswer ?? ""}
                                                                                onChange={(e) => setAnswers((a) => ({ ...a, [q.questionId]: e.target.value }))}
                                                                            />
                                                                            <div className="d-flex gap-2">
                                                                                <Button color="primary" size="sm" onClick={() => resolve(q, "Add")}>
                                                                                    Add to knowledge base
                                                                                </Button>
                                                                                <Button color="secondary" size="sm" outline onClick={() => resolve(q, "Ignore")}>
                                                                                    Not worth answering
                                                                                </Button>
                                                                            </div>
                                                                        </div>
                                                                    ))
                                                                )}
                                                            </div>

                                                            {/* Type the month in until the SMS side writes it */}
                                                            <div className="mt-3">
                                                                {entry?.companyId === c.companyId ? (
                                                                    <div className="p-2" style={qBox}>
                                                                        <div className="f-w-700 mb-2" style={{ fontSize: 13 }}>
                                                                            {MONTHS[month - 1]} {year} — {c.businessName}
                                                                        </div>
                                                                        <div className="row g-2">
                                                                            {FIELDS.map((f) => (
                                                                                <div className="col-6 col-md-3" key={f.key}>
                                                                                    <label className="form-label mb-1" style={{ fontSize: 12 }}>{f.label}</label>
                                                                                    <input type="number" className="form-control form-control-sm"
                                                                                        value={entry[f.key] ?? ""}
                                                                                        onChange={(e) => setEntry((x: any) => ({ ...x, [f.key]: e.target.value }))} />
                                                                                </div>
                                                                            ))}
                                                                            <div className="col-6 col-md-3">
                                                                                <label className="form-label mb-1" style={{ fontSize: 12 }}>Campaign</label>
                                                                                <select className="form-select form-select-sm"
                                                                                    value={entry.campaign}
                                                                                    onChange={(e) => setEntry((x: any) => ({ ...x, campaign: e.target.value }))}>
                                                                                    <option>All</option>
                                                                                    <option>Activation</option>
                                                                                    <option>Reactivation</option>
                                                                                </select>
                                                                            </div>
                                                                        </div>
                                                                        <div className="d-flex gap-2 mt-2">
                                                                            <Button color="primary" size="sm" disabled={saving} onClick={saveActivity}>
                                                                                {saving ? "Saving…" : "Save the month"}
                                                                            </Button>
                                                                            <Button color="link" size="sm" onClick={() => setEntry(null)}>Cancel</Button>
                                                                        </div>
                                                                    </div>
                                                                ) : (
                                                                    <Button color="secondary" size="sm" outline
                                                                        onClick={() => setEntry({ companyId: c.companyId, campaign: "All" })}>
                                                                        Enter this month by hand
                                                                    </Button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        )}
                                    </Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                <small className="text-muted d-block mt-2">
                    The broker sees the same report for their own business only, with no cost or margin anywhere on it.
                </small>
            </div>
        </div>
    );
};

const FIELDS = [
    { key: "messagesSent", label: "Sent" },
    { key: "replies", label: "Replies" },
    { key: "conversations", label: "Conversations" },
    { key: "qualified", label: "Qualified" },
    { key: "booked", label: "Booked" },
    { key: "showedUp", label: "Showed up" },
    { key: "settled", label: "Settled" },
    { key: "escalations", label: "Escalations" },
    { key: "optOuts", label: "Opt outs" },
];

const panel = {
    borderRadius: "8px",
    boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
    backgroundColor: "#fff",
} as const;

const coachBox = {
    borderRadius: 8,
    border: "1px solid #ffe9a8",
    background: "#fffdf3",
} as const;

const qBox = {
    borderRadius: 8,
    border: "1px solid #e6e8f0",
    background: "#fff",
} as const;

const Tile = ({ label, value, strong }: { label: string; value: any; strong?: boolean }) => (
    <div style={{
        border: "1px solid #e6e8f0", borderRadius: 8, padding: "10px 16px",
        background: strong ? "#f0f9f2" : "#fff", minWidth: 120,
    }}>
        <div style={{ fontSize: 11.5, color: "#767d95", fontWeight: 600, letterSpacing: 0.3 }}>{label}</div>
        <div style={{ fontSize: 19, fontWeight: 800, color: strong ? "#1a7f37" : "#111528" }}>{value}</div>
    </div>
);

export default CRMSoniaReports;
